import { blue, blueGrey, brown, grey, purple, red, teal } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

export const ALL_MODES = ["dark", "light"];
type ModeTuple = typeof ALL_MODES;
export type Mode = ModeTuple[number];

// Single source of truth for assignment status keys
// Add new statuses HERE - type augmentation derives from this
export const ASSIGNMENT_STATUS_KEYS = [
	"incoming",
	"preparation",
	"active",
	"paused",
	"sentToClient",
	"toBeClosed",
	"doneSuccess",
	"doneFailed",
	"inactive",
] as const;

export type AssignmentStatusKey = (typeof ASSIGNMENT_STATUS_KEYS)[number];
export type AssignmentStatusColors = Record<AssignmentStatusKey, string>;

/** Background + text colour for one chip, as palette.modelColor carries. */
export interface ChipTone {
	bg: string;
	text: string;
}

/**
 * The manifest row kinds whose chip the MUI `color` union cannot express: all
 * seven of its names are already spoken for by the rows above them
 * (frontend/components/docs/workflow-manifest-fence.tsx), and these three still
 * have to read as three different things.
 */
export type ManifestKindColors = Record<
	"newSetting" | "settingChange" | "celeryBeatChange" | "none",
	ChipTone
>;

// Conditionally select colors based on theme mode
const modeColors = (mode: Mode) => {
	if (mode === "dark") {
		return {
			background: "#1e1e1e", // Adobe-style darker background
			info: "#252525", // Adobe panel background
			infoLight: "#323232", // Adobe lighter panel
			lighterError: "#ff6b6b",
			logo: {
				small: "#5e63b8",
			},
			highlights: {
				experience: "#1a4a5c", // Cool blue-teal
				language: "#5c2a2a", // Muted dark red
			},
			actionButtons: {
				thumbDown: { dark: red[600], main: red[400] },
				undo: { dark: grey[600], main: grey[400], light: grey[200] },
			},
			scrollbar: {
				background: "#1e1e1e",
				thumb: "#4a4a4a",
				thumbFocus: "#6a6a6a",
			},
			mermaidGraph: {
				rectFillColor: "#323232",
				rectBorderColor: "#4a9eff",
				textColor: "#e0e0e0",
				nodeTextColor: "#000000", // Always dark - for text on bright colored node backgrounds
				linkColor: "#e0e0e0",
				arrowHeadColor: "#e0e0e0",
				highlightColor: "#c1e1c1", // Soft green highlight for mermaid rows
			},
			board: {
				columnBackground: "#252525", // Adobe panel bg
				columnHeaderBackground: "#1e1e1e", // Adobe main bg
				dropTargetBackground: "#3d5a80", // Muted blue accent
				iconColor: "#8a8a8a",
				borderColor: "#3c3c3c", // Adobe border color
				titleColor: "#e0e0e0",
				inProgress: "#736700", // Dark yellow-olive for in-progress backgrounds
			},
			chat: {
				outgoing: "#53932A", // Green for sent messages
				incoming: "#383e95", // Blue for received messages
				sent: "#FFEB3B", // Yellow 500 - bright, clear yellow for dark backgrounds
				messageText: "#ffffff", // White text on colored bubbles
				timestamp: grey[500], // Grey for timestamps
			},
			celery: {
				started: "#3e948b", // Teal for STARTED state
				retry: "#c08b3b", // Bronze for RETRY state
			},
			riskFlag: {
				main: "#e91e63", // Pink-red risk indicator
				light: "#ffe0f0", // Soft pink background
			},
		};
	} else {
		return {
			background: "#F5F5F5", // Light grey background for subtlety
			info: blue[100],
			infoLight: blue[200],
			lighterError: "#E57373",
			logo: {
				small: grey[100],
			},
			highlights: {
				experience: blue[100],
				language: red[300],
			},
			actionButtons: {
				thumbDown: { dark: red[700], main: red[600] }, // Darker for better contrast
				undo: { dark: grey[800], main: grey[600], light: grey[300] }, // Darker for better contrast
			},
			scrollbar: {
				background: "#ffffff",
				thumb: grey[400], // Darker for better visibility
				thumbFocus: grey[500],
			},
			mermaidGraph: {
				rectFillColor: "#ECECFF",
				rectBorderColor: "#9370DB",
				textColor: "#000000",
				nodeTextColor: "#000000", // Always dark - for text on bright colored node backgrounds
				linkColor: "#333333",
				arrowHeadColor: "#333333",
				highlightColor: "#c1e1c1", // Soft green highlight for mermaid rows
			},
			board: {
				columnBackground: "#ffffff",
				columnHeaderBackground: grey[100],
				dropTargetBackground: blueGrey[200],
				iconColor: grey[700],
				borderColor: grey[300],
				titleColor: grey[900],
				inProgress: "#736700", // Dark yellow-olive for in-progress backgrounds
			},
			chat: {
				outgoing: "#4CAF50", // Softer green for light mode
				incoming: "#5C6BC0", // Softer indigo for light mode
				sent: "#FBC02D", // Yellow 700 - warm yellow with good contrast, distinct from orange
				messageText: "#ffffff", // White text on colored bubbles
				timestamp: grey[600], // Darker grey for light mode
			},
			celery: {
				started: "#3e948b", // Teal for STARTED state
				retry: "#c08b3b", // Bronze for RETRY state
			},
			riskFlag: {
				main: "#e91e63", // Pink-red risk indicator
				light: "#ffe0f0", // Soft pink background
			},
		};
	}
};

export const themeSettings = (mode: Mode): any => {
	// pick colors here
	// https://materialui.co/colors

	const colors = modeColors(mode);

	const palette = {
		mode: mode,
		primary:
			mode === "dark"
				? { main: "#4a9eff", light: "#7eb8ff", dark: "#2979cc" }
				: { main: "#1976d2", light: "#42a5f5", dark: "#1565c0" },
		secondary:
			mode === "dark"
				? { main: "#b39ddb", light: "#cfb8e8", dark: "#8e7aaf" }
				: { main: "#9c27b0", light: "#ba68c8", dark: "#7b1fa2" },
		success:
			mode === "dark"
				? { main: "#43a047", light: "#4caf50", dark: "#338a36" }
				: { main: "#2e7d32", light: "#4caf50", dark: "#1b5e20" },
		info:
			mode === "dark"
				? { main: "#4dd0e1", light: "#80deea", dark: "#00acc1" }
				: { main: "#0288d1", light: "#03a9f4", dark: "#01579b" },
		warning:
			mode === "dark"
				? { main: "#ffa726", light: "#ffcc80", dark: "#e68a00" }
				: { main: "#ed6c02", light: "#ff9800", dark: "#e65100" },
		error:
			mode === "dark"
				? { main: "#f44336", light: "#ff7961", dark: "#590000" }
				: { main: "#d32f2f", light: "#ef5350", dark: "#590000" },
		// Board movements - amber/gold that works in both modes
		board: {
			main: mode === "dark" ? "#FFB74D" : "#E65100",
		},
		// The model chip matches the CLI statusline (.claude/statusline.py
		// MODEL_COLORS) so a model reads the same in the terminal and the app.
		//
		// Only the last two are guaranteed: 212/214 come from the xterm 256
		// colour cube, whose RGB is fixed. The first three are ANSI 33/35/36 —
		// basic colours every terminal theme redefines — so these are Windows
		// Terminal's default Campbell scheme, which is what this machine
		// renders. Change the terminal scheme and those three drift; pinning
		// statusline.py to 256-colour codes would remove the dependency.
		//
		// Same in light and dark on purpose: matching the terminal is the
		// point, so each carries its own contrast text rather than shifting.
		modelColor: {
			opus: { bg: "#881798", text: "#ffffff" }, // ANSI 35, Campbell purple
			sonnet: { bg: "#3a96dd", text: "#000000" }, // ANSI 36, Campbell cyan
			fable: { bg: "#c19c00", text: "#000000" }, // ANSI 33, Campbell yellow
			haiku: { bg: "#ff87d7", text: "#000000" }, // xterm 212 pink (fixed)
			unknown: { bg: "#ffaf00", text: "#000000" }, // xterm 214 orange (fixed)
		},
		// Three change-manifest chips that could not stay inside the MUI Chip
		// colour union: two settings rows that used to share grey with "nothing
		// touched", and the Celery Beat row, which is the loudest chip in the
		// table on purpose (see workflow-manifest-fence.tsx for the grading).
		// Each mode carries the shade that keeps its own text legible — every
		// pair below is at or above 5:1, the lightest fills taking black text
		// the way MUI's own dark-mode warning chip does.
		manifestKind: {
			newSetting:
				mode === "dark"
					? { bg: blueGrey[300], text: "#000000" }
					: { bg: blueGrey[600], text: "#ffffff" },
			settingChange:
				mode === "dark"
					? { bg: brown[300], text: "#000000" }
					: { bg: brown[600], text: "#ffffff" },
			celeryBeatChange:
				mode === "dark"
					? { bg: purple.A400, text: "#000000" }
					: { bg: purple.A700, text: "#ffffff" },
			// "Nothing touched" is a statement, not the absence of one, so it
			// cannot wear grey: grey now means only "nobody has classified
			// this kind". Teal is the quietest hue left and the furthest from
			// info's cyan, which a `none` row can never sit beside anyway — a
			// manifest that touches nothing is a single row.
			none:
				mode === "dark"
					? { bg: teal[300], text: "#000000" }
					: { bg: teal[700], text: "#ffffff" },
		},
		action: {
			disabled:
				mode === "dark" ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
			disabledBackground:
				mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
		},
		background: {
			default: colors.background,
		},
		DataGrid: {
			bg: mode === "dark" ? "#1e1e1e" : "#ffffff",
			pinnedBg: mode === "dark" ? "#252525" : "#ffffff",
			headerBg: mode === "dark" ? "#252525" : "#ffffff",
		},
	};

	// assignmentStatus (7) + palette.warning.dark + palette.*.main (6) + chartGreen = 15.
	const chartColors = [
		mode === "dark" ? "#FFD54F" : "#FFE082",
		palette.success.light,
		palette.info.light,
		palette.info.main,
		palette.warning.dark,
		palette.success.main,
		palette.error.light,
		mode === "dark" ? grey[400] : grey[300],
		palette.primary.main,
		palette.secondary.main,
		palette.success.dark,
		palette.info.main,
		palette.warning.main,
		palette.error.main,
		"#90ed7d", // Bright lime green for charts
	];

	return {
		palette,
		typography: {
			fontFamily:
				"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
		},
		status: {
			info: colors.info,
			infoLight: colors.infoLight,
			lighterError: colors.lighterError,
			updating: "#000072", // Navy blue for "Updating" status indicators
		},
		riskFlag: {
			main: colors.riskFlag.main,
			light: colors.riskFlag.light,
		},
		celery: {
			started: colors.celery.started,
			retry: colors.celery.retry,
		},
		logo: {
			small: colors.logo.small,
		},
		highlights: {
			experience: colors.highlights.experience,
			language: colors.highlights.language,
		},
		actionButtons: {
			thumbDown: {
				dark: colors.actionButtons.thumbDown.dark,
				main: colors.actionButtons.thumbDown.main,
			},
			undo: {
				dark: colors.actionButtons.undo.dark,
				main: colors.actionButtons.undo.main,
				light: colors.actionButtons.undo.light,
			},
		},
		mermaidGraph: {
			rectFillColor: colors.mermaidGraph.rectFillColor,
			rectBorderColor: colors.mermaidGraph.rectBorderColor,
			textColor: colors.mermaidGraph.textColor,
			nodeTextColor: colors.mermaidGraph.nodeTextColor,
			linkColor: colors.mermaidGraph.linkColor,
			arrowHeadColor: colors.mermaidGraph.arrowHeadColor,
			highlightColor: colors.mermaidGraph.highlightColor,
		},
		board: {
			columnBackground: colors.board.columnBackground,
			columnHeaderBackground: colors.board.columnHeaderBackground,
			dropTargetBackground: colors.board.dropTargetBackground,
			iconColor: colors.board.iconColor,
			borderColor: colors.board.borderColor,
			titleColor: colors.board.titleColor,
			inProgress: colors.board.inProgress,
		},
		assignmentStatus: {
			incoming: palette.secondary.light,
			preparation: mode === "dark" ? "#FFD54F" : "#FFE082",
			active: palette.success.light,
			paused: palette.info.light,
			sentToClient: palette.info.main,
			toBeClosed: palette.warning.dark,
			doneSuccess: palette.success.main,
			doneFailed: palette.error.light,
			inactive: mode === "dark" ? grey[400] : grey[300],
		},
		chat: {
			outgoing: colors.chat.outgoing,
			incoming: colors.chat.incoming,
			queued: palette.warning.light,
			sent: colors.chat.sent,
			messageText: colors.chat.messageText,
			timestamp: colors.chat.timestamp,
		},
		voterColors: [
			palette.success.main,
			palette.success.light,
			palette.success.dark,
			palette.info.main,
			palette.primary.light,
		],
		chartColors,
		components: {
			MuiChartDataProvider: {
				defaultProps: {
					colors: chartColors,
				},
			},
			MuiTooltip: {
				defaultProps: {
					arrow: true,
				},
				styleOverrides: {
					tooltip: {
						backgroundColor: mode === "dark" ? "#323232" : "#ffffff",
						color: mode === "dark" ? "#e0e0e0" : "#333333",
						fontSize: "0.8125rem",
						fontWeight: 400,
						padding: "10px 14px",
						borderRadius: 8,
						boxShadow:
							mode === "dark"
								? "0 4px 20px rgba(0, 0, 0, 0.5)"
								: "0 4px 20px rgba(0, 0, 0, 0.15)",
						border:
							mode === "dark" ? "1px solid #4a4a4a" : "1px solid #e0e0e0",
						maxWidth: "none",
					},
					arrow: {
						color: mode === "dark" ? "#323232" : "#ffffff",
						"&::before": {
							border:
								mode === "dark"
									? "1px solid #4a4a4a"
									: "1px solid #e0e0e0",
						},
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						scrollbarColor: `${colors.scrollbar.thumb} ${colors.scrollbar.background}`,
						"&::-webkit-scrollbar, & *::-webkit-scrollbar": {
							backgroundColor: colors.scrollbar.background,
						},
						"&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
							borderRadius: 8,
							backgroundColor: colors.scrollbar.thumb,
							minHeight: 24,
							border: `3px solid ${colors.scrollbar.background}`,
						},
						"&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus":
							{
								backgroundColor: colors.scrollbar.thumbFocus,
							},
						"&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active":
							{
								backgroundColor: colors.scrollbar.thumbFocus,
							},
						"&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
							{
								backgroundColor: colors.scrollbar.thumbFocus,
							},
						"&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
							backgroundColor: colors.scrollbar.thumb,
						},
					},
				},
			},
			MuiPaper: {
				defaultProps: {
					variant: "outlined",
				},
				styleOverrides: {
					root: {
						padding: "8px",
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						backgroundColor: "inherit",
					},
				},
			},
			MuiChip: {
				defaultProps: {
					variant: "outlined",
				},
				styleOverrides: {
					root: {
						borderRadius: 4,
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					root: ({ theme }: { theme: any }) => ({
						borderBottom: `1px solid ${theme.palette.divider}`,
						"& .MuiTabs-indicator": {
							height: 4,
							borderRadius: "2px 2px 0 0",
							backgroundColor: theme.palette.primary.main,
						},
					}),
				},
			},
			MuiTab: {
				styleOverrides: {
					root: ({ theme }: { theme: any }) => ({
						textTransform: "none" as const,
						fontWeight: 500,
						fontSize: "0.875rem",
						color: theme.palette.text.primary,
						opacity: 0.7,
						borderRadius: "2px 2px 0 0",
						"& .MuiTouchRipple-root": {
							color: theme.palette.primary.main,
						},
						"&:hover": {
							backgroundColor: theme.palette.action.hover,
							opacity: 1,
						},
						"&.Mui-selected": {
							color: theme.palette.primary.main,
							opacity: 1,
							fontWeight: 600,
						},
					}),
				},
			},
		},
	};
};

export const setTheme = (mode = "light") => {
	return createTheme(themeSettings(mode));
};
